import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import "@/styles/sidebar-painel-header.css";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Home, 
  MessageSquare, 
  FileText, 
  Wand2, 
  BarChart3, 
  Settings, 
  LogOut,
  User,
  CreditCard,
  Bell,
  Calculator,
  Scale,
  BookOpen,
  ChevronDown,
  Briefcase,
  Share2,
  Bot,
  Coins,
  HardDrive,
  Pin,
  PinOff
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CreditsUsage } from "@/components/CreditsUsage";
import StorageUsage from "@/components/StorageUsage";
import { useNotifications } from "@/hooks/useNotifications";
import SkyToggle from "@/components/ui/sky-toggle";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";

const navigationItems = [
  {
    title: "Documentação",
    url: "/documentacao",
    icon: BookOpen,
  },
];

const aiToolsItems = [
  {
    title: "Jurisprudências",
    url: "/jurisprudencias",
    icon: Scale,
  },
  {
    title: "Casos / Clientes",
    url: "/casos",
    icon: Briefcase,
  },
  {
    title: "Chat",
    url: "/chat-juridico",
    icon: MessageSquare,
  },
  {
    title: "Assessor",
    url: "/assessor",
    icon: Bot,
  },
  {
    title: "Melhore seu texto",
    url: "/melhorar-texto",
    icon: Wand2,
  },
  {
    title: "Cálculos",
    url: "/calculos",
    icon: Calculator,
  },
];

const configItems = [
  {
    title: "Configurações",
    url: "/configuracoes",
    icon: Settings,
  },
  {
    title: "Planos e Assinaturas",
    url: "/planos",
    icon: CreditCard,
  },
  {
    title: "Papel Timbrado",
    url: "/papel-timbrado",
    icon: BarChart3,
  },
  {
    title: "Legal",
    url: "/juridico",
    icon: Scale,
  },
];

const sidebarVariants = {
  open: { width: "15rem" },
  closed: { width: "4rem" },
};

const textVariants = {
  open: { opacity: 1, display: "block", transition: { delay: 0.1, duration: 0.15 } },
  closed: { opacity: 0, display: "none", transition: { duration: 0.1 } },
};

export function AppSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { unreadCount } = useNotifications();
  const isDesktop = () => window.innerWidth >= 1280;
  const [isCollapsed, setIsCollapsed] = useState(() => !isDesktop());
  const [isPinned, setIsPinned] = useState(() => isDesktop());

  useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth >= 1280;
      setIsCollapsed(!desktop);
      setIsPinned(desktop);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const [isPainelExpanded, setIsPainelExpanded] = useState(false);
  const [isToolsExpanded, setIsToolsExpanded] = useState(true);
  const [isConfigExpanded, setIsConfigExpanded] = useState(false);

  const iconSize = isCollapsed ? "h-6 w-6" : "h-5 w-5";

  const isActive = (url: string) => {
    if (url === "/chat-docs") return location.pathname === "/chat-docs";
    if (url === "/chat-juridico") return location.pathname === "/chat-juridico" || location.pathname.startsWith("/chat-juridico/");
    if (url === "/chat") return location.pathname === "/chat" || (location.pathname.startsWith("/chat/") && !location.pathname.startsWith("/chat-docs") && !location.pathname.startsWith("/chat-juridico"));
    return location.pathname === url;
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/");
    } catch (error) {
      console.error("Erro ao fazer logout:", error);
    }
  };

  const getInitials = (name: string) => {
    return name.split(" ").map((part) => part[0]).join("").toUpperCase().substring(0, 2);
  };

  const SidebarItem = ({ icon: Icon, title, url, className = "" }: { icon: any; title: string; url: string; className?: string }) => {
    const active = isActive(url);
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={() => navigate(url)}
            className={`
              flex items-center gap-3 w-full rounded-lg px-2.5 py-2 text-sm font-medium transition-colors
              ${active ? 'bg-primary/10 text-primary' : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'}
              ${className}
            `}
          >
            <Icon className={`${iconSize} shrink-0`} />
            <motion.span
              variants={textVariants}
              animate={isCollapsed ? "closed" : "open"}
              className="truncate whitespace-nowrap"
            >
              {title}
            </motion.span>
          </button>
        </TooltipTrigger>
        {isCollapsed && (
          <TooltipContent side="right" sideOffset={8}>
            {title}
          </TooltipContent>
        )}
      </Tooltip>
    );
  };

  return (
    <TooltipProvider delayDuration={100}>
      <motion.div
        className="hidden md:flex flex-col h-full min-h-0 border-r bg-sidebar shrink-0 overflow-hidden"
        variants={sidebarVariants}
        animate={isCollapsed ? "closed" : "open"}
        transition={{ type: "tween", ease: "easeOut", duration: 0.2 }}
        onMouseEnter={() => !isPinned && setIsCollapsed(false)}
        onMouseLeave={() => !isPinned && setIsCollapsed(true)}
      >
        {/* Header */}
        <div className="border-b p-3 flex items-center justify-between shrink-0">
          <div className="flex-1 flex justify-center">
            {isCollapsed ? (
              <img src="/logo_laib_sidebar_collapsed.png" alt="Laib" className="h-[4.5rem] w-[4.5rem] object-contain" />
            ) : (
              <img src="/logo_justicai_sidebar.png" alt="Justiçai Logo" className="h-12 w-auto" />
            )}
          </div>
          {!isCollapsed && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => {
                    setIsPinned(!isPinned);
                    if (!isPinned) setIsCollapsed(false);
                  }}
                  className={`p-1.5 rounded-lg transition-colors shrink-0 ${isPinned ? 'bg-primary/10 text-primary' : 'text-sidebar-foreground/60 hover:bg-sidebar-accent'}`}
                >
                  {isPinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={8}>
                {isPinned ? 'Desafixar sidebar' : 'Fixar sidebar aberta'}
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* Content */}
        <ScrollArea className="flex-1">
          <div className="px-2 py-3 space-y-1">
            {/* Painel header */}
            <div
              onClick={() => !isCollapsed && setIsPainelExpanded(!isPainelExpanded)}
              className={`sidebar-section-header ${!isCollapsed ? 'cursor-pointer' : ''}`}
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <motion.span
                  variants={textVariants}
                  animate={isCollapsed ? "closed" : "open"}
                  className="text-xs font-semibold uppercase text-sidebar-foreground/60 tracking-wider truncate"
                >
                  Painel
                </motion.span>
              </div>
              {!isCollapsed && (
                <div className="flex items-center gap-1">
                  <ChevronDown className={`h-3.5 w-3.5 text-sidebar-foreground/60 transition-transform ${isPainelExpanded ? 'rotate-180' : ''}`} />
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate('/notificacoes'); }}
                    className="relative p-1 rounded hover:bg-sidebar-accent transition-colors"
                    aria-label="Notificações"
                  >
                    <Bell className="h-3.5 w-3.5 text-sidebar-foreground/60" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] px-0.5 bg-destructive text-white rounded-full text-[9px] font-semibold flex items-center justify-center leading-none">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* Notificações - visible as regular item when collapsed */}
            {isCollapsed && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => navigate('/notificacoes')}
                    className="flex items-center gap-3 w-full rounded-lg px-2.5 py-2 text-sm font-medium transition-colors text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                  >
                    <span className="relative shrink-0">
                      <Bell className={`${iconSize}`} />
                      {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] px-0.5 bg-destructive text-white rounded-full text-[9px] font-semibold flex items-center justify-center leading-none">
                          {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                      )}
                    </span>
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" sideOffset={8}>Notificações</TooltipContent>
              </Tooltip>
            )}

            {/* Compartilhamento - always visible */}
            <SidebarItem icon={Share2} title="Compartilhamento" url="/compartilhamento" />

            {/* Collapsible Painel items */}
            <AnimatePresence>
              {(isPainelExpanded && !isCollapsed) && navigationItems.map((item) => (
                <motion.div
                  key={item.url}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  className="overflow-hidden"
                >
                  <SidebarItem icon={item.icon} title={item.title} url={item.url} />
                </motion.div>
              ))}
            </AnimatePresence>

            {/* Ferramentas com IA */}
            <div
              onClick={() => !isCollapsed && setIsToolsExpanded(!isToolsExpanded)}
              className={`sidebar-section-header ${!isCollapsed ? 'cursor-pointer' : ''}`}
            >
              <motion.span
                variants={textVariants}
                animate={isCollapsed ? "closed" : "open"}
                className="text-xs font-semibold uppercase text-sidebar-foreground/60 tracking-wider truncate"
              >
                Ferramentas com IA
              </motion.span>
              {!isCollapsed && (
                <ChevronDown className={`h-3.5 w-3.5 text-sidebar-foreground/60 transition-transform shrink-0 ${isToolsExpanded ? 'rotate-180' : ''}`} />
              )}
            </div>

            <AnimatePresence>
              {(isToolsExpanded || isCollapsed) && aiToolsItems.map((item) => (
                <motion.div
                  key={item.url}
                  initial={!isCollapsed ? { height: 0, opacity: 0 } : false}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  className="overflow-hidden"
                >
                  <SidebarItem icon={item.icon} title={item.title} url={item.url} />
                </motion.div>
              ))}
            </AnimatePresence>

            {/* Config */}
            <div
              onClick={() => !isCollapsed && setIsConfigExpanded(!isConfigExpanded)}
              className={`sidebar-section-header ${!isCollapsed ? 'cursor-pointer' : ''}`}
            >
              <motion.span
                variants={textVariants}
                animate={isCollapsed ? "closed" : "open"}
                className="text-xs font-semibold uppercase text-sidebar-foreground/60 tracking-wider truncate"
              >
                Config. Planos e Infos.
              </motion.span>
              {!isCollapsed && (
                <ChevronDown className={`h-3.5 w-3.5 text-sidebar-foreground/60 transition-transform shrink-0 ${isConfigExpanded ? 'rotate-180' : ''}`} />
              )}
            </div>

            <AnimatePresence>
              {(isConfigExpanded || isCollapsed) && configItems.map((item) => (
                <motion.div
                  key={item.url}
                  initial={!isCollapsed ? { height: 0, opacity: 0 } : false}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  className="overflow-hidden"
                >
                  <SidebarItem icon={item.icon} title={item.title} url={item.url} />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </ScrollArea>

        {/* Footer */}
        <div className="mt-auto border-t shrink-0">
          {/* Theme toggle + logo */}
          <div className={`flex items-center ${isCollapsed ? 'flex-col gap-2 py-2 px-1' : 'justify-between py-2 px-3'}`}>
            {!isCollapsed && (
              <a href="https://laib.com.br" target="_blank" rel="noopener noreferrer" className="transition-opacity hover:opacity-80">
                <img src="/logo_ia_laib_novo-09-03.png" alt="LAIB Logo" className="h-9 w-auto" />
              </a>
            )}
            <SkyToggle className={`${isCollapsed ? 'scale-[0.5]' : 'scale-[0.6]'}`} />
          </div>

          {/* Credits & Storage */}
          {isCollapsed ? (
            <div className="flex flex-col items-center gap-1.5 px-1 pb-2">
              <CreditsUsage variant="icon" />
              <StorageUsage variant="icon" />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-1.5 px-2 pb-2">
              <CreditsUsage compact />
              <StorageUsage compact />
            </div>
          )}

          {/* User */}
          <div className="p-2 border-t">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                {user?.avatar_url ? (
                  <img src={user.avatar_url} alt={user.display_name || user.email} className="h-8 w-8 rounded-full shrink-0" />
                ) : (
                  <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                    <span className="text-sm font-medium">
                      {user && getInitials(user.display_name || user.email)}
                    </span>
                  </div>
                )}
                {!isCollapsed && (
                  <div className="flex flex-col overflow-hidden">
                    <span className="text-sm font-medium truncate text-sidebar-foreground">{user?.display_name || user?.email}</span>
                    <span className="text-xs text-muted-foreground truncate">{user?.email}</span>
                  </div>
                )}
              </div>
              {!isCollapsed && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-sidebar-foreground">
                      <Settings className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => navigate("/configuracoes")}>
                      <User className="mr-2 h-4 w-4" />
                      Configurações
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                      <LogOut className="mr-2 h-4 w-4" />
                      Sair
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </TooltipProvider>
  );
}
